import React, { useState, useEffect, useRef } from 'react';
import { updateUserStats } from '../utils/firebase';
import { initSpeechRecognition, findMatches } from '../utils/speechRecognition';

export default function ActiveSpeechScreen({ 
  user, 
  items, 
  variationsCache, 
  completedItems, 
  setCompletedItems, 
  onComplete 
}) {
  const [isListening, setIsListening] = useState(false);
  const [timer, setTimer] = useState(0);
  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);

  // Dynamic sizing based on item count
  const getItemStyles = (count) => {
    const baseFontSize = 1.8;
    const basePadding = 22;
    const baseMargin = 14;
    const minFontSize = 0.82;
    const minPadding = 9;
    const minMargin = 3;
    const factor = Math.min((count - 1) / 15, 1);
    
    return {
      fontSize: `${baseFontSize - (baseFontSize - minFontSize) * factor}em`,
      padding: `${basePadding - (basePadding - minPadding) * factor}px`,
      marginBottom: `${baseMargin - (baseMargin - minMargin) * factor}px`
    };
  };

  const itemStyles = getItemStyles(items.length);

  // Timer effect
  useEffect(() => {
    let interval;
    if (isListening) {
      interval = setInterval(() => setTimer(t => t + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [isListening]);

  // Format timer
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle speech result
  const handleSpeechResult = (transcript) => {
    console.log('Transcript:', transcript);
    
    const matches = findMatches(transcript, items, variationsCache);
    
    if (matches.size > 0) {
      setCompletedItems(prev => {
        const updated = new Set(prev);
        matches.forEach(index => updated.add(index));
        return updated;
      });
      console.log(`Matched ${matches.size} items`);
    }
  };

  // Handle speech error
  const handleSpeechError = (error) => {
    console.error('Speech recognition error:', error);
    if (error === 'not-allowed') {
      alert('Microphone permission denied. Please allow microphone access and try again.');
      stopListening();
    }
  };

  // Start listening
  const startListening = () => {
    if (!recognitionRef.current) {
      const recognition = initSpeechRecognition(handleSpeechResult, handleSpeechError);
      
      if (!recognition) {
        alert('Speech recognition not supported in this browser. Please use Chrome, Edge, or Safari.');
        return;
      }

      recognition.onend = () => {
        if (isListeningRef.current) {
          recognition.start();
        }
      };

      recognitionRef.current = recognition;
    }

    try {
      recognitionRef.current.start();
      setIsListening(true);
      isListeningRef.current = true;
      console.log('Started listening...');
    } catch (error) {
      console.error('Failed to start recognition:', error);
    }
  };

  // Stop listening
  const stopListening = () => {
    if (recognitionRef.current) {
      isListeningRef.current = false;
      recognitionRef.current.stop();
      setIsListening(false);
      console.log('Stopped listening');
    }
  };

  // End speech
  const handleEndSpeech = async () => {
    stopListening();
    
    // Update user stats in Firebase
    if (user) {
      try {
        await updateUserStats(user.uid, items.length, timer);
      } catch (error) {
        console.error('Failed to update stats:', error);
      }
    }
    
    onComplete(timer);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        isListeningRef.current = false;
        recognitionRef.current.stop();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8 relative">
      <div className="absolute top-6 right-6 text-4xl font-bold text-purple-500">
        {formatTime(timer)}
      </div>

      <div className="max-w-5xl mx-auto pt-20">
        <div className="mb-6 flex gap-4">
          {!isListening ? (
            <button
              onClick={startListening}
              className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-green-800 transition"
            >
              Start Listening
            </button>
          ) : (
            <button
              onClick={stopListening}
              className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-3 rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition"
            >
              Pause
            </button>
          )}
          <button
            onClick={handleEndSpeech}
            className="bg-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-600 transition"
          >
            End Speech
          </button>
        </div>

        {isListening && (
          <div className="mb-6 text-green-500 font-semibold flex items-center gap-2">
            <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
            Listening...
          </div>
        )}

        <div className="space-y-1">
          {items.map((item, index) => (
            <div
              key={index}
              style={{
                fontSize: itemStyles.fontSize,
                padding: itemStyles.padding,
                marginBottom: itemStyles.marginBottom
              }}
              className={`rounded-xl font-medium bg-gray-800 border-l-4 transition-all ${
                completedItems.has(index)
                  ? 'border-green-500 opacity-50 line-through'
                  : 'border-purple-500'
              }`}
            >
              {completedItems.has(index) && (
                <span className="text-green-500 mr-2">✓</span>
              )}
              <span className={completedItems.has(index) ? 'text-gray-500' : 'text-white'}>
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}